import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import '../styles/pages/Competition.scss';
import '../styles/components/index.scss';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import ShareIcon from '@mui/icons-material/Share';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import slackLogo from '../images/partners/online-ticket-seller.png';
import figmaLogo from '../images/partners/kick-it-out-logo.png';
import jiraLogo from '../images/partners/Playsport-logo.png';

const Competition = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    teamName: '',
    teamRelationship: '',
    teamType: 'player', // Default to player
    howHeard: '',
    agreeToTerms: false
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    
    setFormData({
      ...formData,
      [name]: val
    });
    
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
    
    if (submitStatus) {
      setSubmitStatus(null);
    }
  };
  
  const validateForm = () => {
    const errors = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email.trim())) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!formData.mobile.trim()) {
      errors.mobile = 'Mobile number is required';
    } else if (!/^[0-9\s+()-]{10,15}$/.test(formData.mobile.trim())) {
      errors.mobile = 'Please enter a valid mobile number';
    }
    
    if (!formData.teamName.trim()) {
      errors.teamName = 'Team name is required';
    }
    
    if (!formData.teamRelationship.trim()) {
      errors.teamRelationship = 'Please select your relationship to the team';
    }
    
    if (!formData.agreeToTerms) {
      errors.agreeToTerms = 'You must agree to the terms to participate';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setFormErrors({});
    
    try {
      console.log('Competition entry being submitted:', formData);
      
      const requestData = {
        ...formData,
        email: formData.email,
        contestType: 'adidas-predators',
        captchaToken: 'competition-page'
      };
      
      const response = await fetch('/api/competition-entry.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestData),
      });
      
      let data;
      let errorMessage;
      
      try {
        const textResponse = await response.text();
        
        try {
          data = JSON.parse(textResponse);
        } catch (parseError) {
          errorMessage = 'Server returned invalid response format';
          throw new Error(errorMessage);
        }
      } catch (textError) {
        errorMessage = 'Failed to read server response';
        throw new Error(errorMessage);
      }
      
      if (!response.ok) {
        errorMessage = data?.error || `Server error: ${response.status}`;
        throw new Error(errorMessage);
      }
      
      // Success handling
      setSubmitStatus('success');
      
      // Reset form after successful submission
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        mobile: '',
        teamName: '',
        teamRelationship: '',
        teamType: 'player',
        howHeard: '',
        agreeToTerms: false
      });
      setFormErrors({});
      
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      setFormErrors(prev => ({
        ...prev,
        submit: error.message || 'Failed to submit entry. Please try again.'
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="competition-page">
      <Helmet>
        <title>Win a pair of Adidas Predator Football Boots | FirstWhistle Competition</title>
        <meta name="description" content="Enter our exclusive competition to win a pair of Adidas Predator football boots. Join FirstWhistle for your chance to win." />
        <meta property="og:title" content="Win a pair of Adidas Predator Football Boots | FirstWhistle Competition" />
        <meta property="og:description" content="Enter our exclusive competition to win a pair of Adidas Predator football boots. Join FirstWhistle for your chance to win." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://firstwhistle.app/win-pair-of-adidas-predators" />
        <meta property="og:image" content="https://firstwhistle.app/images/adidas-predator.jpg" />
        <link rel="canonical" href="https://firstwhistle.app/win-pair-of-adidas-predators" />
      </Helmet>
      
      <header>
        {/* Bokeh element removed */}
      </header>
      
      <div className="comp-container">
        <section className="comp-hero">
          <div className="comp-hero-content">
            <h1>Win a pair of Adidas Predator Football Boots</h1>
          </div>
          
          {submitStatus === 'success' && (
            <div className="comp-success">
              <h2>Thanks for entering!</h2>
              <p>Your entry has been submitted and you're now in the draw to win a pair of Adidas Predator boots. We'll notify the winner after the closing date.</p>
              
              {formData.teamType === 'coach' && (
                <p className="coach-share-prompt">
                  <ShareIcon style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Share FirstWhistle with your coaching network! Our AI-powered player stats analysis and smart availability tracking go beyond basic team management, helping you make data-driven decisions for optimal team selection.
                </p>
              )}
              
              {formData.teamType === 'parent' && (
                <p className="coach-share-prompt">
                  <ShareIcon style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  No more lost WhatsApp messages about match locations and training times! Share FirstWhistle with your coach to keep all fixtures, locations, and player availability in one organised place that everyone can access instantly.
                </p>
              )}
              
              {formData.teamType === 'player' && (
                <p className="coach-share-prompt">
                  <ShareIcon style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Help your team get better organised! Share FirstWhistle with your coach and teammates to make tracking fixtures, availability, and stats much easier for everyone.
                </p>
              )}
              
              <div className="comp-success-share">
                <a 
                  href={`https://wa.me/?text=Check out FirstWhistle, the AI-powered football team management app that's making grassroots football easier for everyone: https://firstwhistle.app`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="comp-share-button whatsapp"
                >
                  <WhatsAppIcon style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Share via WhatsApp
                </a>
                <a 
                  href={`mailto:?subject=FirstWhistle - AI Football Team Management&body=Hi there,\n\nI thought you might be interested in FirstWhistle, the AI-powered football team management app that's making grassroots football easier for everyone.\n\nCheck it out at: https://firstwhistle.app\n\nBest,`}
                  className="comp-share-button email"
                >
                  <EmailIcon style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Share via Email
                </a>
              </div>
              
              <p className="comp-signoff">Thanks again for your support!</p>
              <p className="comp-signoff-name">- Dean & the FirstWhistle Team</p>
            </div>
          )}
        </section>
        
        {/* Display success message for debugging */}
        <section className="comp-hero" style={{ display: "none", marginTop: "30px" }}>
          <div className="comp-success">
            <h2>Thanks for entering!</h2>
            <p>Your entry has been submitted and you're now in the draw to win a pair of Adidas Predator boots. We'll notify the winner after the closing date.</p>
            
            {/* Parent Message */}
            <p className="coach-share-prompt">
              <ShareIcon style={{ verticalAlign: 'middle', marginRight: '8px' }} />
              No more lost WhatsApp messages about match locations and training times! Share FirstWhistle with your coach to keep all fixtures, locations, and player availability in one organised place that everyone can access instantly.
            </p>
            
            <div className="comp-success-share">
              <a 
                href={`https://wa.me/?text=Check out FirstWhistle, the AI-powered football team management app that's making grassroots football easier for everyone: https://firstwhistle.app`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="comp-share-button whatsapp"
              >
                <WhatsAppIcon style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                Share via WhatsApp
              </a>
              <a 
                href={`mailto:?subject=FirstWhistle - AI Football Team Management&body=Hi there,\n\nI thought you might be interested in FirstWhistle, the AI-powered football team management app that's making grassroots football easier for everyone.\n\nCheck it out at: https://firstwhistle.app\n\nBest,`}
                className="comp-share-button email"
              >
                <EmailIcon style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                Share via Email
              </a>
            </div>
            
            <p className="comp-signoff">Thanks again for your support!</p>
            <p className="comp-signoff-name">- Dean & the FirstWhistle Team</p>
          </div>
        </section>
        
        {submitStatus !== 'success' && (
          <section className="comp-form-section" id="competition-form">
            <div className="comp-form-container">
              <h2>Enter the Competition</h2>
              <p className="comp-subtitle">Complete the form below to enter our free prize draw</p>
              
              <form className="comp-form" onSubmit={handleSubmit}>
                {submitStatus === 'error' && formErrors.submit && (
                  <div className="comp-global-error">
                    {formErrors.submit}
                  </div>
                )}
                
                <div className="comp-form-row">
                  <div className="comp-form-group">
                    <label htmlFor="firstName">First Name</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="Your first name"
                      className={formErrors.firstName ? 'error' : ''}
                      disabled={isSubmitting}
                    />
                    {formErrors.firstName && <span className="comp-error-message">{formErrors.firstName}</span>}
                  </div>
                  
                  <div className="comp-form-group">
                    <label htmlFor="lastName">Last Name</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Your last name"
                      className={formErrors.lastName ? 'error' : ''}
                      disabled={isSubmitting}
                    />
                    {formErrors.lastName && <span className="comp-error-message">{formErrors.lastName}</span>}
                  </div>
                </div>
                
                <div className="comp-form-row">
                  <div className="comp-form-group">
                    <label htmlFor="email">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Your email address"
                      className={formErrors.email ? 'error' : ''}
                      disabled={isSubmitting}
                    />
                    {formErrors.email && <span className="comp-error-message">{formErrors.email}</span>}
                  </div>
                  
                  <div className="comp-form-group">
                    <label htmlFor="mobile">Mobile</label>
                    <input
                      type="tel"
                      id="mobile"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      placeholder="Your mobile number"
                      className={formErrors.mobile ? 'error' : ''}
                      disabled={isSubmitting}
                    />
                    {formErrors.mobile && <span className="comp-error-message">{formErrors.mobile}</span>}
                  </div>
                </div>
                
                <div className="comp-form-group">
                  <label htmlFor="teamName">Team/Club Name (include age group if applicable)</label>
                  <input
                    type="text"
                    id="teamName"
                    name="teamName"
                    value={formData.teamName}
                    onChange={handleChange}
                    placeholder="e.g., Hastings United U12 / Hastings School of Football"
                    className={formErrors.teamName ? 'error' : ''}
                    disabled={isSubmitting}
                  />
                  {formErrors.teamName && <span className="comp-error-message">{formErrors.teamName}</span>}
                </div>
                
                <div className="comp-form-group">
                  <label htmlFor="teamRelationship">Your relationship to this team</label>
                  <select
                    id="teamRelationship"
                    name="teamRelationship"
                    value={formData.teamRelationship}
                    onChange={handleChange}
                    className={formErrors.teamRelationship ? 'error' : ''}
                    disabled={isSubmitting}
                  >
                    <option value="">Please select...</option>
                    <option value="player">I play for this team</option>
                    <option value="coach">I coach/manage this team</option>
                    <option value="parent">My child plays for this team</option>
                    <option value="admin">I'm an administrator for this team</option>
                    <option value="other">Other</option>
                  </select>
                  {formErrors.teamRelationship && <span className="comp-error-message">{formErrors.teamRelationship}</span>}
                </div>
                
                <div className="comp-form-group">
                  <label htmlFor="howHeard">How did you hear about us?</label>
                  <input
                    type="text"
                    id="howHeard"
                    name="howHeard"
                    value={formData.howHeard}
                    onChange={handleChange}
                    placeholder="Instagram, Facebook, Friend, etc."
                    className={formErrors.howHeard ? 'error' : ''}
                    disabled={isSubmitting}
                  />
                  {formErrors.howHeard && <span className="comp-error-message">{formErrors.howHeard}</span>}
                </div>
                
                <div className="comp-form-group">
                  <label>I am a:</label>
                  <div className="radio-group">
                    <label>
                      <input
                        type="radio"
                        name="teamType"
                        value="player"
                        checked={formData.teamType === 'player'}
                        onChange={handleChange}
                        disabled={isSubmitting}
                      />
                      Player
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="teamType"
                        value="parent"
                        checked={formData.teamType === 'parent'}
                        onChange={handleChange}
                        disabled={isSubmitting}
                      />
                      Parent
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="teamType"
                        value="coach"
                        checked={formData.teamType === 'coach'}
                        onChange={handleChange}
                        disabled={isSubmitting}
                      />
                      Coach/Admin
                    </label>
                  </div>
                </div>
                
                <div className="comp-checkbox-group">
                  <input
                    type="checkbox"
                    id="agreeToTerms"
                    name="agreeToTerms"
                    checked={formData.agreeToTerms}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                  <label htmlFor="agreeToTerms">
                    I am happy to be contacted in the future regarding FirstWhistle updates and competitions
                  </label>
                  {formErrors.agreeToTerms && <span className="comp-error-message">{formErrors.agreeToTerms}</span>}
                </div>
                
                <button 
                  type="submit" 
                  className="comp-submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Enter Competition'}
                </button>
                
                <div className="comp-terms-section">
                  <h4>Competition Terms and Conditions</h4>
                  <div className="comp-terms-details">
                    Competition closes 30th September 2025. Winner will be selected at random from all valid entries.
                    One entry per person. UK residents only. Entrants must be 16 years or older.
                    Prize consists of one pair of Adidas Predator football boots in winner's selected size (subject to availability) up to the value of £100.
                    No cash alternative available. The winner will be notified by email/SMS within 14 days of the closing date.
                    Promoter: Media Meadow Ltd trading as FirstWhistle. Your personal data will not be shared with any third parties.
                  </div>
                </div>
                
                <div className="comp-recaptcha-info">
                  <p>This site is protected by reCAPTCHA</p>
                </div>
              </form>
            </div>
          </section>
        )}
        
        <section className="comp-partners">
          <h3>Trusted by football organisations:</h3>
          <div className="comp-partners-grid">
            <img src={jiraLogo} alt="Playsport" />
            <img src={slackLogo} alt="Online Ticket Seller" />
            <img src={figmaLogo} alt="Kick It Out" />
          </div>
        </section>
      </div>
    </div>
  );
};

export default Competition; 